<template>
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-gray-50 dark:bg-gray-700">
        <tr>
          <th v-if="selectable" class="px-6 py-3 text-left">
            <input type="checkbox" :checked="isAllSelected" :indeterminate="isSomeSelected" class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" @change="handleSelectAll" />
          </th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">标题</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">分类</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">难度</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">单词数</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">状态</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">操作</th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        <tr v-for="book in books" :key="book.id" class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          <td v-if="selectable" class="px-6 py-4">
            <input type="checkbox" :checked="selectedIds.includes(book.id)" class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" @change="handleSelect(book.id)" />
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ book.id }}</td>
          <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
            <div class="font-medium">{{ book.title }}</div>
            <div v-if="book.description" class="text-gray-500 dark:text-gray-400 text-xs mt-1 line-clamp-1">{{ book.description }}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
            <div v-if="book.category">{{ book.category.name }}</div>
            <div v-if="book.subcategory" class="text-gray-500 dark:text-gray-400 text-xs">{{ book.subcategory.name }}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
            <div class="flex items-center">
              <span v-for="i in 5" :key="i" class="text-yellow-400">{{ i <= book.difficultyLevel ? '★' : '☆' }}</span>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ book.contentCount || 0 }}</td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="flex flex-col gap-1">
              <span :class="['inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', book.isPublic ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300']">{{ book.isPublic ? '公开' : '私有' }}</span>
              <span :class="['inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', book.isFree ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400']">{{ book.isFree ? '免费' : '付费' }}</span>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div class="flex items-center gap-2">
              <button class="link-btn-edit" @click="$emit('view', book)">查看</button>
              <button class="link-btn-view" @click="$emit('edit', book)">编辑</button>
              <button class="link-btn-delete" @click="$emit('delete', book)">删除</button>
            </div>
          </td>
        </tr>
        <tr v-if="books.length === 0">
          <td :colspan="selectable ? 8 : 7" class="px-6 py-12 text-center text-gray-500 dark:text-gray-400">暂无数据</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { Book } from '@/types/dictation/book';

interface Props {
  books: Book[];
  selectable?: boolean;
  selectedIds?: number[];
}

const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  selectedIds: () => [],
});

const emit = defineEmits<{
  'update:selectedIds': [ids: number[]];
  'view': [book: Book];
  'edit': [book: Book];
  'delete': [book: Book];
}>();

const isAllSelected = computed(() => props.books.length > 0 && props.selectedIds.length === props.books.length);
const isSomeSelected = computed(() => props.selectedIds.length > 0 && props.selectedIds.length < props.books.length);

const handleSelectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  emit('update:selectedIds', checked ? props.books.map(book => book.id) : []);
};

const handleSelect = (id: number) => {
  const index = props.selectedIds.indexOf(id);
  const newIds = [...props.selectedIds];
  if (index > -1) newIds.splice(index, 1);
  else newIds.push(id);
  emit('update:selectedIds', newIds);
};
</script>
